import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Section from './Section';
import { fetchPartners, fetchPartnersSection } from '../lib/sanity';
import { defaultPartners, defaultPartnersSection } from '../lib/defaultContent';
import { useSanityContent } from '../hooks/useSanityContent';
import type { PartnersSectionContent } from '../lib/types';

interface PartnersGridProps {
  partners?: Array<{ name: string; logo: string }>;
}

const PartnersGrid: React.FC<PartnersGridProps> = ({ partners }) => {
  const [data, setData] = useState(partners || defaultPartners);
  const { content } = useSanityContent<PartnersSectionContent>(
    fetchPartnersSection,
    defaultPartnersSection
  );

  useEffect(() => {
    if (partners && partners.length > 0) return;
    fetchPartners().then((result) => {
      if (result && result.length > 0) {
        setData(result);
      }
    });
  }, [partners]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  return (
    <Section padding="default">
      <div className="text-center mb-10 sm:mb-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
          {content.title}
        </h2>
        <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
          {content.description}
        </p>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto"
      >
        {data.map((partner, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            className="bg-cream-50 rounded-lg p-4 sm:p-6 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <img
              src={partner.logo}
              alt={partner.name}
              className="max-h-16 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300"
            />
          </motion.div>
        ))}
      </motion.div>
    </Section>
  );
};

export default PartnersGrid;
